/**
 * WHICH LEG IS MISSING, AND WHAT THE READER IS TOLD ABOUT IT.
 *
 * ⛔⛔ WHY THIS FILE EXISTS — MEASURED ON THE OWNER'S SERVED PAGE, 2026-08-23 ~11:4xZ, HUDCHAT-6.
 * `SessionCvdPane` guarded ONE of its two inputs:
 * ```
 *   {#if !bars.length}  ->  "NO CVD BARS — a gap, not a zero"      LOUD, correct
 *   candles             ->  NO GUARD AT ALL
 * ```
 * Kill-a-feed on the SERVED page, with the read-API's REAL error shape (HTTP 400 + a VALID JSON
 * body `{ok:false,...}`, so `fetch` does not throw and `res.json()` SUCCEEDS):
 * ```
 *   cvd leg dead ...... "NO CVD BARS" rendered.        price pane ink 10,191 px
 *   candles leg dead .. NOTHING rendered.              price pane ink ** 0 px, BLANK **
 *                       header still read "290 cvd bars · 0 price bars — served, not fixtured"
 * ```
 * ⇒ ***THE PAGE ASSERTED IT WAS SERVED WHILE A LEG WAS DEAD, AND THE PANE WENT BLANK RATHER THAN
 * AMBER.*** Law 1 (fail-LOUD, "no empty-on-error panels") and the seat's kill-a-feed gate
 * ("panel shows amber STALE, never blank").
 *
 * ⭐ AND THE REASON IT IS A PURE FUNCTION rather than more `{#if}` in the markup: `feedError.ts`
 * banked this repo's own law one shift earlier — ***A DECISION NOTHING CAN TEST IS A DECISION
 * NOTHING DID TEST.*** The markup is not reachable by node's strip-only runner; this is.
 */

export type FeedLeg = "cvd" | "price";

export interface PaneGap {
  leg: FeedLeg;
  /** The loud line. Short enough to read at a glance on a heads-up surface. */
  title: string;
  /** Why it is a gap and not a zero — the distinction the CVD notice already made. */
  detail: string;
}

export interface PaneCounts {
  /** CVD bars actually served for the window. */
  bars: number;
  /** OHLC candles actually served for the window. */
  candles: number;
}

/**
 * Every leg that has nothing to draw, in a stable order.
 *
 * ⚠ THE LEGS ARE REPORTED INDEPENDENTLY, AND THAT IS THE WHOLE POINT. A caller that collapses
 * both into one boolean re-creates the defect: with `bars` healthy and `candles` dead the old
 * pane took the success path and painted an empty price pane in silence.
 *
 * ⛔ A FIXTURE WHERE BOTH LEGS ARE EMPTY CANNOT DISCRIMINATE "guards both" FROM "guards cvd
 * only" — the two rules agree on it. The arms therefore drive the ASYMMETRIC cases, which is
 * exactly the fixture-saturation trap HUDCHAT-4 banked (his `c[0].open === 0` passed because
 * "opens at zero" and "opens at its own value" were the same number).
 */
export function paneGaps(counts: PaneCounts): PaneGap[] {
  const gaps: PaneGap[] = [];
  if (!(counts.bars > 0)) {
    gaps.push({
      leg: "cvd",
      title: "NO CVD BARS",
      detail: "nothing served for this window — a gap, not a zero",
    });
  }
  if (!(counts.candles > 0)) {
    gaps.push({
      leg: "price",
      title: "NO PRICE BARS",
      detail: "price unserved for this window — the pane is empty, not flat",
    });
  }
  return gaps;
}

/**
 * May a surface claim its data is served?
 *
 * ⛔ NOT `gaps.length === 0 && loaded`. The claim is about the DATA, so it is answered by the
 * data alone — a page that says "served, not fixtured" beside "0 price bars" is making a false
 * statement in its own header, which is what the served page did.
 */
export function mayClaimServed(counts: PaneCounts): boolean {
  return paneGaps(counts).length === 0;
}
